import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Layout from 'Components/Layout';
import { Services } from 'Services';
import { TextField, Button, Typography, Box, Select, MenuItem } from '@mui/material';
import { BsCloudUpload } from 'react-icons/bs';
import UserClasses from '../users/Users.module.css';
import AddProductClasses from '../products/Products.module.css';
import OrderClasses from '../orders/Orders.module.css';
import { Success, Error_Dark } from 'Util/Toast';
import CommonTutorClasses from '../commoncssfiles/CategoryTutor.module.css';
import { RxCross2 } from 'react-icons/rx';
import ReactLoading from 'react-loading';
import ProductClasses from '../products/Products.module.css';

const EditProduct = () => {
    const { productId } = useParams();
    const navigate = useNavigate();
    const [product, setProduct] = useState(null);
    const [loading, setLoading] = useState(true);
    const [imageUploading, setImageUploading] = useState(false);
    const [categories, setCategories] = useState([]);

    // States for multiple image handling
    const [imageUrls, setImageUrls] = useState([]);
    const [imageFiles, setImageFiles] = useState([]);

    const [locationId, setLocationId] = useState('');

    // Modified filehandleChange function to use UploadImages API
    // Modified filehandleChange function to use UploadImages API
    const filehandleChange = async (event) => {
        const files = event.target.files;
        if (!files || files.length === 0) return;

        setImageUploading(true);

        try {
            // Create FormData with all selected files
            const formData = new FormData();

            // Add folder parameter only once
            formData.append("folder", "product-images");

            // Append all files to FormData
            for (let i = 0; i < files.length; i++) {
                const currentFile = files[i];

                if (
                    currentFile.type === 'image/png' ||
                    currentFile.type === 'image/jpeg' ||
                    currentFile.type === 'image/webp'
                ) {
                    formData.append('images', currentFile);
                } else {
                    alert(`${currentFile.name} is not a valid image format. Only PNG, JPEG, and WebP are accepted.`);
                    setImageUploading(false);
                    return;
                }
            }

            // Upload all images at once using UploadImages API
            const uploadResponse = await Services.UploadImages(formData);

            if (uploadResponse.Status === 1) {
                // Access the Files array from your API response
                const uploadedImages = uploadResponse.Files || [];

                // Add new images to existing ones using the correct property names
                setImageUrls(prev => [...prev, ...uploadedImages.map(img => img.ImageURL)]);
                setImageFiles(prev => [...prev, ...uploadedImages.map(img => img.ImageID)]);

                // Update the product state with all image filenames
                setProduct(prevProduct => ({
                    ...prevProduct,
                    images: [...imageFiles, ...uploadedImages.map(img => img.ImageID)]
                }));

                Success('Images uploaded successfully!');
            } else {
                Error_Dark(uploadResponse.Message || 'Failed to upload images');
            }

        } catch (error) {
            console.error('Error uploading images:', error);
            Error_Dark('Error uploading images');
        } finally {
            setImageUploading(false);
        }
    };

    useEffect(() => {
        const fetchCategories = async () => {
            try {
                const token = { Authorization: `token ${localStorage.getItem('Token')}` };
                const response = await Services.getCategories(token);
                if (response.Status === 1) {
                    setCategories(response.categories);
                } else {
                    console.error('Error fetching categories:', response.Message);
                    Error_Dark(response.Message || 'Failed to fetch categories');
                }
            } catch (error) {
                console.error('Error fetching categories:', error);
                Error_Dark('Error loading categories');
            }
        };

        fetchCategories();
    }, []);

    // Handle removal of an image
    const handleRemoveImage = (index) => {
        // Remove the image at the specified index
        const updatedImageUrls = imageUrls.filter((_, i) => i !== index);
        const updatedImageFiles = imageFiles.filter((_, i) => i !== index);

        setImageUrls(updatedImageUrls);
        setImageFiles(updatedImageFiles);

        // Update the product state with the new image file list
        setProduct(prevProduct => ({
            ...prevProduct,
            images: updatedImageFiles
        }));
    };

    // Fetch product details on component mount
    useEffect(() => {
        const fetchProduct = async () => {
            try {
                const token = { Authorization: `token ${localStorage.getItem('Token')}` };
                const response = await Services.getProductById(productId, token);

                if (response.Status === 1) {
                    setProduct({ ...response.productDetails });
                    setLocationId(response.productDetails.locationId);

                    // Handle existing images
                    // Check if images is an array or a string
                    if (Array.isArray(response.productDetails.images)) {
                        // If it's already an array, use it directly
                        setImageFiles(response.productDetails.images);

                        // Convert filenames to full URLs for display
                        const urls = response.productDetails.images.map(img => {
                            return response.productDetails.url.replace(
                                response.productDetails.images[0],
                                img
                            );
                        });
                        setImageUrls(urls);
                    } else {
                        // If it's a single string, convert to array
                        setImageFiles([response.productDetails.images]);
                        setImageUrls([response.productDetails.url]);
                    }
                } else {
                    console.error('Error fetching product:', response.Message);
                    Error_Dark(response.Message || 'Failed to fetch product details');
                }
            } catch (error) {
                console.error('Error fetching product:', error);
                Error_Dark('Error loading product details');
            } finally {
                setLoading(false);
            }
        };

        fetchProduct();
    }, [productId]);

    // Handle input change for form fields
    const handleInputChange = (event) => {
        const { name, value } = event.target;

        // Check if the name matches the nutritional value pattern
        const match = name.match(/^(key|value|unit)(\d+)$/);

        if (match) {
            // If the name matches, handle nutritional value change
            const [fieldType, index] = match.slice(1);

            setProduct((prevProduct) => {
                const updatedNutritionalValues = prevProduct.nutritionalValues.map((nutrition, idx) => {
                    if (idx === parseInt(index, 10)) {
                        return {
                            ...nutrition,
                            [fieldType]: value,
                        };
                    }
                    return nutrition;
                });

                return {
                    ...prevProduct,
                    nutritionalValues: updatedNutritionalValues,
                };
            });
        } else {
            // Else, handle other fields directly
            setProduct((prevProduct) => ({
                ...prevProduct,
                [name]: value,
            }));
        }
    };

    // Handle location change
    const handleLocationChange = (event) => {
        setLocationId(event.target.value);
    };

    // Save product changes
    const handleSave = async () => {
        if (imageFiles.length === 0) {
            Error_Dark('At least one product image is required');
            return;
        }

        try {
            const token = { Authorization: `token ${localStorage.getItem('Token')}` };
            const payload = {
                categoryId: product.categoryId,
                productName: product.productName,
                images: imageFiles, // Now sending array of image filenames
                description: product.description,
                status: product.status,
                nutritionalValues: product.nutritionalValues,
                locationId: locationId,
            };

            setLoading(true);
            const response = await Services.editProduct(productId, payload, token);

            if (response.Status === 1) {
                Success('Product updated successfully');
                navigate('/products');
            } else {
                Error_Dark(response.Message || 'Failed to update product');
            }
        } catch (error) {
            console.error('Error updating product:', error);
            Error_Dark('Failed to update product. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    // Show loading state while fetching product
    if (loading || !product) {
        return <div>Loading...</div>;
    }

    // File upload button design (updated to match AddProduct)
    const fileuploadDesign = (
        <div className={CommonTutorClasses["add-item"]} onClick={() => document.getElementById("image").click()}>
            <div className={CommonTutorClasses['upload-image']}>
                {imageUrls.length === 0 && (
                    <div className={CommonTutorClasses['add-item']}>
                        <BsCloudUpload className={CommonTutorClasses['upload-icon']} />
                        <h5>Drag Your Files here</h5>
                        <p>(Only *.jpeg, *.png, *.webp will be accepted)</p>
                    </div>
                )}
                <input
                    type="file"
                    id="image"
                    onChange={filehandleChange}
                    style={{ display: 'none' }}
                    multiple // Enable multiple file selection
                    accept="image/png,image/jpeg,image/webp"
                />
            </div>
        </div>
    );

    // Component to display multiple images with remove option
    const MultipleImagesPreview = () => (
        <div className={ProductClasses['image-preview-container']} style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '10px',
            marginTop: '15px'
        }}>
            {imageUrls.map((url, index) => (
                <div key={index} style={{
                    position: 'relative',
                    width: '120px',
                    height: '120px',
                    marginBottom: '10px'
                }}>
                    <img
                        src={url}
                        alt={`Product ${index + 1}`}
                        style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            borderRadius: '4px'
                        }}
                    />
                    <button
                        type="button"
                        onClick={() => handleRemoveImage(index)}
                        style={{
                            position: 'absolute',
                            top: '-10px',
                            right: '-10px',
                            background: 'white',
                            border: '1px solid #ddd',
                            borderRadius: '50%',
                            width: '25px',
                            height: '25px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            padding: 0
                        }}
                    >
                        <RxCross2 />
                    </button>
                </div>
            ))}
            {imageUploading && (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '10px 0' }}>
                    <ReactLoading type="spin" color="#000" height={30} width={30} />
                    <span style={{ marginLeft: '10px' }}>Uploading...</span>
                </div>
            )}
        </div>
    );

    // Render component UI
    return (
        <Layout Active={'Products'}>
            <Helmet>
                <meta charSet="utf-8" />
                <title>Edit Product</title>
            </Helmet>
            <div className={UserClasses['main-first-container']}>
                <div className={UserClasses['main-data-tab']}>
                    <div className={UserClasses['add-users-btn']}>
                        <Button variant="contained" color="primary" onClick={() => navigate(-1)}>
                            BACK
                        </Button>
                    </div>
                    <div className={UserClasses['users-main']}>
                        <h2 className={OrderClasses['header-form']}>Edit Product</h2>
                        <form className={AddProductClasses['product-form']} data-aos="fade-up"
                            data-aos-anchor-placement="top-bottom">
                            <div className={AddProductClasses['main-prod-add-new']}>
                                <Box style={{ width: '400px' }} className={CommonTutorClasses['form-control']}>
                                    <label htmlFor="productName">Product Name</label>
                                    <input
                                        type="text"
                                        className={CommonTutorClasses['text-input']}
                                        value={product.productName || ''}
                                        onChange={handleInputChange}
                                        name="productName"
                                        required
                                    />
                                </Box>
                                <Box style={{ width: '400px' }} className={CommonTutorClasses['form-control']}>
                                    <label htmlFor="categoryId">Category</label>
                                    <Select
                                        value={product.categoryId || ''}
                                        onChange={(e) => handleInputChange({ target: { name: 'categoryId', value: e.target.value } })}
                                        displayEmpty
                                        className={CommonTutorClasses['text-input']}
                                        style={{ height: '40px' }}
                                        inputProps={{ 'aria-label': 'Select Category' }}
                                    >
                                        <MenuItem value="">Select Category</MenuItem>
                                        {categories.map((category) => (
                                            <MenuItem key={category.categoryId} value={category.categoryId}>
                                                {category.categoryName}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </Box>
                                <Box style={{ width: '400px' }} className={CommonTutorClasses['form-control']}>
                                    <label htmlFor="location">Location</label>
                                    <Select
                                        style={{ height: '40px' }}
                                        value={locationId}
                                        onChange={handleLocationChange}
                                        displayEmpty
                                        inputProps={{ 'aria-label': 'Select Location' }}
                                    >
                                        <MenuItem value={1}>Store</MenuItem>
                                        <MenuItem value={2}>Inventory</MenuItem>
                                        <MenuItem value={3}>Both Store & Inventory</MenuItem>
                                    </Select>
                                </Box>
                                <Box style={{ width: '100%' }} className={CommonTutorClasses['form-control']}>
                                    <label htmlFor="description">Description</label>
                                    <textarea
                                        style={{ textIndent: '10px' }}
                                        rows={10}
                                        className={ProductClasses['text-area']}
                                        value={product.description || ''}
                                        onChange={handleInputChange}
                                        name="description"
                                        required
                                    />
                                </Box>
                            </div>

                            <Box style={{ marginTop: '10px' }} className={CommonTutorClasses['file-upload-wrapper']}>
                                <header>
                                    <h3>
                                        Upload Product Images <span className="important">*</span>
                                    </h3>
                                    <p style={{ fontSize: '0.85rem', color: '#666' }}>
                                        Select multiple images by holding Ctrl (or Cmd) while selecting
                                    </p>
                                </header>
                                <div className={CommonTutorClasses["upload-image"]} required>
                                    {fileuploadDesign}
                                </div>
                                {/* Display uploaded images */}
                                <MultipleImagesPreview />
                            </Box>

                            {/* Nutritional values */}
                            <Typography variant="subtitle1" className={AddProductClasses["image-label-two"]}>
                                Nutritional Values:
                            </Typography>
                            {product.nutritionalValues && product.nutritionalValues.length > 0 && (
                                product.nutritionalValues.map((nutrition, index) => (
                                    <div className={CommonTutorClasses['form-control']} key={index}>
                                        <Box className={AddProductClasses['main-prod-add-new']}>
                                            <input
                                                type="text"
                                                className={CommonTutorClasses['text-input']}
                                                value={nutrition.key || ''}
                                                onChange={handleInputChange}
                                                name={`key${index}`}
                                                required
                                                style={{ padding: '10px', width: '27%', marginTop: '10px' }}
                                            />
                                            <input
                                                type="text"
                                                className={CommonTutorClasses['text-input']}
                                                value={nutrition.value || ''}
                                                onChange={handleInputChange}
                                                name={`value${index}`}
                                                required
                                                style={{ padding: '10px', width: '27%', marginTop: '10px' }}
                                            />
                                            <Select
                                                value={nutrition.unit || ''}
                                                onChange={(e) => handleInputChange({ target: { name: `unit${index}`, value: e.target.value } })}
                                                displayEmpty
                                                style={{ width: '27%', height: '49px', marginTop: '10px' }}
                                            >
                                                <MenuItem value="">Select Unit</MenuItem>
                                                <MenuItem value="g">Grams (g)</MenuItem>
                                                <MenuItem value="mg">Milligrams (mg)</MenuItem>
                                                <MenuItem value="µg">Micrograms (µg)</MenuItem>
                                                <MenuItem value="mcg">Micrograms (mcg)</MenuItem>
                                                <MenuItem value="IU">International Units (IU)</MenuItem>
                                                <MenuItem value="RE">Retinol Equivalents (RE)</MenuItem>
                                                <MenuItem value="DFE">Dietary Folate Equivalents (DFE)</MenuItem>
                                                <MenuItem value="kcal">Kilocalories (kcal)</MenuItem>
                                                <MenuItem value="kJ">Kilojoules (kJ)</MenuItem>
                                                <MenuItem value="mEq">Milliequivalents (mEq)</MenuItem>
                                                <MenuItem value="ppm">Parts Per Million (ppm)</MenuItem>
                                                <MenuItem value="mL">Milliliters (mL)</MenuItem>
                                                <MenuItem value="µmol">Micromoles (µmol)</MenuItem>
                                                <MenuItem value="eq">Equivalents (eq)</MenuItem>
                                            </Select>
                                        </Box>
                                    </div>
                                ))
                            )}

                            {/* Save and Cancel buttons */}
                            <div className={AddProductClasses['button-container']}>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={handleSave}
                                    disabled={loading || imageUploading}
                                >
                                    {loading ? 'Saving...' : 'Save'}
                                </Button>
                                <Button
                                    variant="contained"
                                    color="secondary"
                                    onClick={() => navigate('/products')}
                                    disabled={loading}
                                >
                                    Cancel
                                </Button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </Layout>
    );
};

export default EditProduct;